import queryString from 'query-string';
import { apiSlice } from 'store/api/apiSlice';

import {
  DayStaffSchedule,
  EventDetailsProps,
  GetCombineStaffScheduleItem,
  GetStaffScheduleProps,
  ShiftTypesResponseProps,
  StaffScheduleReqProps
} from './calendar.types';

export const calendarApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    createEvent: build.mutation<
      { data: EventDetailsProps; message: string },
      Partial<EventDetailsProps>
    >({
      invalidatesTags: ['Event'],
      query: (body) => ({
        body: body,
        method: 'POST',
        url: '/events'
      }),
      transformResponse: (response: { data: EventDetailsProps; message: string }) => response
    }),
    deleteEvent: build.mutation<string, { eventId: string }>({
      invalidatesTags: ['Event'],
      query: ({ eventId }) => ({
        method: 'DELETE',
        url: `/events/${eventId}`
      }),
      transformResponse: (response: { message: string }) => response.message
    }),
    editEvent: build.mutation<
      { data: Partial<EventDetailsProps>; message: string },
      { body: Partial<EventDetailsProps>; eventId: string }
    >({
      invalidatesTags: ['Event'],
      query: ({ body, eventId }) => ({
        body: body,
        method: 'PATCH',
        url: `/events/${eventId}`
      }),
      transformResponse: (response: { data: Partial<EventDetailsProps>; message: string }) => ({
        data: response.data,
        message: response.message
      })
    }),
    getCombineStaffSchedule: build.query<GetCombineStaffScheduleItem[], StaffScheduleReqProps>({
      query: ({ params }) => {
        const paramsString = queryString.stringify(params, { arrayFormat: 'bracket' });
        return { url: `/combined-staff-schedule?${paramsString}` };
      },
      transformResponse: (response: { data: GetCombineStaffScheduleItem[] }) => response.data
    }),
    getDayStaffSchedule: build.query<DayStaffSchedule[], StaffScheduleReqProps>({
      query: ({ params }) => {
        const paramsString = queryString.stringify(params, { arrayFormat: 'bracket' });
        return { url: `/staff-schedules?${paramsString}` };
      },
      transformResponse: (response: { data: DayStaffSchedule[] }) => response.data
    }),
    getEvent: build.query<EventDetailsProps, { eventId: string }>({
      keepUnusedDataFor: 0,
      query: ({ eventId }) => ({
        url: `/events/${eventId}`
      }),
      transformResponse: (response: { data: EventDetailsProps }) => response.data
    }),
    getShiftTypes: build.query<ShiftTypesResponseProps[], void>({
      query: () => ({
        url: '/shift-types'
      }),
      transformResponse: (response: { data: ShiftTypesResponseProps[] }) => response.data
    }),
    getStaffSchedule: build.query<
      GetStaffScheduleProps,
      { fromDate: string; timezone: string; toDate: string; userId: string }
    >({
      providesTags: (result) =>
        result
          ? ['Event', ...result.data.map(({ _id }) => ({ id: _id, type: 'Event' as const }))]
          : ['Event'],
      query: ({ fromDate, timezone, toDate, userId }) => ({
        params: {
          fromDate,
          timezone,
          toDate
        },
        url: `/staffs/${userId}/schedule`
      })
    }),
    validateEvent: build.mutation<
      { message: string },
      { body: Partial<EventDetailsProps>; eventId: string }
    >({
      query: ({ body, eventId }) => ({
        body: body,
        method: 'POST',
        url: `/events/${eventId}/validate`
      }),
      transformResponse: (response: { message: string }) => ({ message: response.message })
    })
  })
});

export const {
  useCreateEventMutation,
  useDeleteEventMutation,
  useEditEventMutation,
  useGetEventQuery,
  useGetShiftTypesQuery,
  useLazyGetCombineStaffScheduleQuery,
  useLazyGetDayStaffScheduleQuery,
  useLazyGetEventQuery,
  useLazyGetStaffScheduleQuery,
  useValidateEventMutation
} = calendarApiSlice;
